import { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Switch as _Switch,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import ThemedSwitch from '@/components/ThemedSwitch';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';
import { FONT, SPACE, RADIUS } from '@/constants/tokens';
import { Eye, EyeOff, Lock, Zap } from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '@/lib/logger';

const log = createLogger('VisibilityOptIn');

interface Props {
  visible: boolean;
  userId: string;
  onComplete: () => void;
}

interface VisibilityRow {
  label: string;
  key: 'display_name' | 'handle' | 'strength_score' | 'weight' | 'height' | 'age';
  locked: boolean;
  defaultOn: boolean;
  description?: string;
}

const ROWS: VisibilityRow[] = [
  { label: 'Display Name', key: 'display_name', locked: true, defaultOn: true },
  { label: 'Username / Handle', key: 'handle', locked: true, defaultOn: true },
  { label: 'Strength Score', key: 'strength_score', locked: true, defaultOn: true, description: 'Core ranking mechanic' },
  { label: 'Body Weight', key: 'weight', locked: false, defaultOn: false, description: 'Visible on your profile & leaderboard' },
  { label: 'Height', key: 'height', locked: false, defaultOn: false, description: 'Visible on your profile & leaderboard' },
  { label: 'Age', key: 'age', locked: false, defaultOn: false, description: 'Visible on your profile & leaderboard' },
];

export default function VisibilityOptInModal({ visible, userId, onComplete }: Props) {
  const { theme } = useTheme();
  const [toggles, setToggles] = useState<Record<string, boolean>>({
    weight: false,
    height: false,
    age: false,
  });
  const [saving, setSaving] = useState(false);

  const handleGoLive = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          visibility_weight: toggles.weight ?? false,
          visibility_height: toggles.height ?? false,
          visibility_age: toggles.age ?? false,
          visibility_opted_in: true,
        })
        .eq('id', userId);

      if (error) {
        log.error('Failed to save visibility preferences:', error);
      }

      await AsyncStorage.setItem(`duel:visibility_shown:${userId}`, 'true');
      onComplete();
    } catch (err) {
      log.error('Visibility save exception:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent statusBarTranslucent>
      <View style={styles.overlay}>
        <View style={[styles.container, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.headerSection}>
              <View style={[styles.iconCircle, { backgroundColor: theme.colors.accent + '20' }]}>
                <Eye size={28} color={theme.colors.accent} />
              </View>
              <Text style={[styles.title, { color: theme.colors.text, fontFamily: TYPO.heading.bold }]}>
                You're About to Go Live
              </Text>
              <Text style={[styles.subtitle, { color: theme.colors.textSecondary, fontFamily: TYPO.body.regular }]}>
                To view the leaderboard, you'll be on it too. Choose what details other lifters can see.
              </Text>
            </View>

            {/* Toggle rows */}
            <View style={styles.rowsContainer}>
              {ROWS.map((row) => {
                const isOn = row.locked ? row.defaultOn : (toggles[row.key] ?? false);
                return (
                  <View
                    key={row.key}
                    style={[styles.row, { borderBottomColor: theme.colors.border }]}
                  >
                    <View style={styles.rowLeft}>
                      {row.locked ? (
                        <Lock size={16} color={theme.colors.textTertiary} />
                      ) : isOn ? (
                        <Eye size={16} color={theme.colors.accent} />
                      ) : (
                        <EyeOff size={16} color={theme.colors.textTertiary} />
                      )}
                      <View style={styles.rowText}>
                        <Text style={[styles.rowLabel, { color: theme.colors.text, fontFamily: TYPO.body.semibold }]}>
                          {row.label}
                        </Text>
                        {row.description ? (
                          <Text style={[styles.rowDesc, { color: theme.colors.textTertiary, fontFamily: TYPO.body.regular }]}>
                            {row.description}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                    <View style={styles.rowRight}>
                      {row.locked ? (
                        <Text style={[styles.alwaysOn, { color: theme.colors.textTertiary, fontFamily: TYPO.body.medium }]}>
                          Always on
                        </Text>
                      ) : (
                        <ThemedSwitch
                          value={isOn}
                          onValueChange={(val) =>
                            setToggles((prev) => ({ ...prev, [row.key]: val }))
                          }
                          trackColor={{ false: theme.colors.border, true: theme.colors.accent + '60' }}
                          thumbColor={isOn ? theme.colors.accent : theme.colors.textTertiary}
                        />
                      )}
                    </View>
                  </View>
                );
              })}
            </View>

            {/* Go Live button */}
            <TouchableOpacity
              style={[styles.goLiveBtn, { backgroundColor: theme.colors.accent }]}
              onPress={handleGoLive}
              disabled={saving}
              activeOpacity={0.8}
            >
              {saving ? (
                <ActivityIndicator color={theme.colors.accentForeground} />
              ) : (
                <>
                  <Zap size={18} color={theme.colors.accentForeground} />
                  <Text style={[styles.goLiveText, { color: theme.colors.accentForeground, fontFamily: TYPO.heading.bold }]}>
                    Go Live
                  </Text>
                </>
              )}
            </TouchableOpacity>

            <Text style={[styles.footer, { color: theme.colors.textTertiary, fontFamily: TYPO.body.regular }]}>
              You can change these anytime in Settings → Privacy.
            </Text>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACE['4'],
  },
  container: {
    width: '100%',
    maxWidth: 420,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    padding: SPACE['6'],
    maxHeight: '85%',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: SPACE['6'],
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: SPACE['4'],
  },
  title: {
    fontSize: FONT['2xl'],
    textAlign: 'center',
    marginBottom: SPACE['2'],
  },
  subtitle: {
    fontSize: FONT.base,
    textAlign: 'center',
    lineHeight: 20,
  },
  rowsContainer: {
    marginBottom: SPACE['6'],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACE['3'],
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE['3'],
    flex: 1,
  },
  rowText: {
    flex: 1,
  },
  rowLabel: {
    fontSize: FONT.md,
  },
  rowDesc: {
    fontSize: FONT.sm,
    marginTop: 2,
  },
  rowRight: {
    marginLeft: SPACE['3'],
  },
  alwaysOn: {
    fontSize: FONT.sm,
  },
  goLiveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE['2'],
    paddingVertical: SPACE['4'],
    borderRadius: RADIUS.md,
    marginBottom: SPACE['3'],
  },
  goLiveText: {
    fontSize: FONT.lg,
    textTransform: 'uppercase',
  },
  footer: {
    fontSize: FONT.xs,
    textAlign: 'center',
  },
});
